/**
 * Single source of truth for a document's coarse, user-facing processing
 * state during ingestion.
 *
 * Today this derives the state on the CLIENT from raw document blobs plus the
 * set of active background jobs. The target end-state (the "full redesign")
 * is for the BACKEND to compute this once and expose `doc.processing_state`
 * directly — at which point `deriveDocState()` collapses to
 * `return doc.processing_state` and every caller keeps working unchanged.
 *
 * Keep this the ONLY place that translates internal pipeline detail (triage /
 * extraction / analysis / indexing / enrichment jobs) into the small
 * user-facing vocabulary below. Do not re-derive ingestion state from raw
 * blobs anywhere else.
 *
 * Leaf module: imports only the scan-badge leaf, so it can be used from any
 * surface without risking a circular-import TDZ blank screen.
 */
import { scanBadge } from '../constants/scanStatus';

// The coarse states a user is shown. Everything between "uploaded" and
// "ready to search" is a single PROCESSING state — the pipeline's internal
// stages are an implementation detail, not a status the user tracks.
export const DOC_STATE = {
  PROCESSING: 'processing',
  READY: 'ready',
  NO_CONTENT: 'no_content',
  FAILED: 'failed',
  BLOCKED: 'blocked',
};

// A background job is its own surfaced action (e.g. a Deep search upgrade,
// full transcription) only when the backend explicitly tagged it
// initiated_by="user". Auto enrichment fan-out (graph/entity extraction) is
// tagged "auto"; legacy untagged jobs are treated as background work too.
export function isUserJob(job) {
  return job?.params?.initiated_by === 'user';
}

// Coarse progress estimate (0..100) for the progress bar only. Intentionally
// NOT surfaced as a label — the user sees "Processing", never the internal
// stage name ("Triaging" / "Analyzing" / "Building knowledge graph").
export function estimateProgress(doc) {
  if ((doc.status || 'unknown') === 'pending') return 5;
  const char = doc.characterization || {};
  const triage = doc.triage || {};
  const extraction = doc.extraction || {};
  const analysis = doc.analysis || {};
  if (analysis.classification || analysis.profile || analysis.summary) return 90;
  if (extraction.method || extraction.method_used) return 70;
  if (triage.strategy) return 50;
  if (char.content_category || char.detected_mime) return 30;
  return 10;
}

/**
 * Derive the coarse user-facing state for a document from its OWN pipeline.
 *
 * Deliberately a pure function of the document's own fields. Post-completion
 * enrichment (vector indexing, graph extraction) runs asynchronously at the
 * CASE level on a separate timeline — it must NOT re-flip a finished document
 * back to PROCESSING. Doing so both double-renders the doc (Processing + Done
 * at once) and reads as "it restarted after it said done". Residual case-level
 * work is surfaced once, at the case level, by the caller.
 *
 * A document is READY when its own processing is done: extracted, classified,
 * viewable. That is an honest "done" for the document; case-wide search/graph
 * finalization is a separate, correctly-scoped signal — not this doc's state.
 */
export function deriveDocState(doc) {
  const scan = scanBadge(doc);
  if (scan && scan.state === 'infected') return DOC_STATE.BLOCKED;
  if (doc.status === 'failed') return DOC_STATE.FAILED;
  if (doc.status === 'completed') {
    return doc.outcome_status === 'empty' ? DOC_STATE.NO_CONTENT : DOC_STATE.READY;
  }
  // pending / processing / scanning / anything mid-pipeline
  return DOC_STATE.PROCESSING;
}
